import { ArrowLeft, CheckCircle2, KeyRound, Loader2, MessageCircleMore, Phone, ShieldCheck } from "lucide-react";
import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Brand } from "../components/ui/Brand";
import { setSessionToken } from "../lib/session";
import { useAccount } from "../context/AccountContext";

export function LoginPage() {
  const navigate = useNavigate();
  const { refresh } = useAccount();
  const [phone, setPhone] = useState("628987654321");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"PHONE" | "OTP">("PHONE");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const requestOtp = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/auth/otp/request", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "OTP_REQUEST_FAILED");
      setStep("OTP");
      setCode(result.demoCode || "");
      setMessage(result.demoCode ? `Mode lokal: kode OTP ${result.demoCode}` : "Kode OTP telah dikirim melalui WhatsApp.");
    } catch (reason) {
      const code = reason instanceof Error ? reason.message : "OTP_REQUEST_FAILED";
      setError(code === "DASHBOARD_ACCESS_REQUIRED" ? "Nomor ini masih memakai paket Siap Digital. Upgrade ke Pro atau Export melalui WhatsApp terlebih dahulu." : "Nomor belum terdaftar atau belum memiliki akses dashboard.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/auth/otp/verify", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ phone, code }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "OTP_INVALID");
      setSessionToken(result.sessionToken);
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch {
      setError("Kode OTP tidak valid atau sudah kedaluwarsa.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_38%),radial-gradient(circle_at_bottom_right,_#ffedd5,_transparent_34%),#f8fafc] p-4 sm:p-8">
      <div className="relative mx-auto grid min-h-[calc(100vh-4rem)] max-w-6xl overflow-hidden rounded-[2.5rem] border border-white/80 bg-white/[.85] shadow-soft backdrop-blur-xl lg:grid-cols-[0.92fr_1.08fr]">
        <section className="hidden bg-gradient-to-br from-brand-700 via-brand-850 to-slate-950 p-10 text-white lg:flex lg:flex-col lg:justify-between">
          <Brand inverse />
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-100"><MessageCircleMore size={14} /> WhatsApp-linked account</span>
            <h1 className="mt-6 text-5xl font-black leading-[1.02] tracking-[-0.055em]">Website yang dibuat lewat chat, langsung muncul di dashboard yang sama.</h1>
            <p className="mt-5 max-w-lg text-base leading-7 text-white/65">Tidak ada migrasi akun. Nomor WhatsApp menjadi identitas awal, sedangkan dashboard hanya membuka fitur baru pada website yang sudah dimiliki.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {["Slug tetap sama", "Produk & QRIS tetap", "Edit chat tetap aktif"].map((item) => (
              <div key={item} className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 text-xs font-bold text-white/75"><CheckCircle2 size={16} className="mb-2 text-accent-300" />{item}</div>
            ))}
          </div>
        </section>

        <section className="flex items-center justify-center p-6 sm:p-10 lg:p-14">
          <div className="w-full max-w-md">
            <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-brand-700"><ArrowLeft size={17} /> Kembali ke landing page</Link>
            <div className="lg:hidden"><Brand /></div>
            <h2 className="mt-8 text-3xl font-black tracking-[-0.04em] text-slate-900 lg:mt-0">Masuk dengan WhatsApp</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">Khusus akun Siap Profesional dan Siap Export. Kode masuk dikirim ke nomor yang sebelumnya digunakan untuk membuat website.</p>

            <form className="mt-8 space-y-5" onSubmit={step === "PHONE" ? requestOtp : verifyOtp}>
              <label className="block">
                <span className="mb-2 block text-sm font-extrabold text-slate-700">Nomor WhatsApp</span>
                <span className="relative block">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input className="field !pl-12" value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="62812..." disabled={step === "OTP"} required />
                </span>
              </label>

              {step === "OTP" && (
                <label className="block">
                  <span className="mb-2 block text-sm font-extrabold text-slate-700">Kode OTP</span>
                  <span className="relative block">
                    <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input className="field !pl-12 tracking-[0.28em]" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="123456" required />
                  </span>
                </label>
              )}

              {message && <div className="rounded-2xl border border-brand-100 bg-brand-50 p-3 text-sm font-bold text-brand-800">{message}</div>}
              {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm font-bold text-rose-700">{error}</div>}

              <button type="submit" className="btn-primary w-full !py-3.5" disabled={loading}>
                {loading ? <Loader2 size={18} className="animate-spin" /> : step === "PHONE" ? <MessageCircleMore size={18} /> : <ShieldCheck size={18} />}
                {loading ? "Memproses..." : step === "PHONE" ? "Kirim kode WhatsApp" : "Masuk ke dashboard"}
              </button>
              {step === "OTP" && <button type="button" onClick={() => { setStep("PHONE"); setCode(""); setMessage(""); }} className="w-full text-sm font-extrabold text-slate-500 hover:text-brand-700">Ganti nomor WhatsApp</button>}
            </form>

            <div className="mt-6 rounded-2xl border border-accent-100 bg-accent-50 p-4">
              <div className="flex gap-3">
                <ShieldCheck className="mt-0.5 shrink-0 text-accent-700" size={19} />
                <div><p className="text-sm font-extrabold text-accent-900">Akun demo lokal</p><p className="mt-1 text-xs leading-5 text-accent-800/75">Nomor: <strong>628987654321</strong>. OTP lokal: <strong>123456</strong>.</p></div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
